import { FC, useRef, useEffect, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from '@frontend/i18n';
import { TextField, FormRow, EmailField, Text } from '@frontend/design-system';
import { useEmailFields } from '../../../../hooks';
import {
  containerStyle,
  lastRowStyle,
  bodyContainerStyle,
  emailBodyFormRowStyle,
  errorMessageStyle,
} from './email-mode.styles';

export const EmailDeliveryMode: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const { emailBody, updateEmailBody, emailSubjectFieldProps, emailFieldProps } = useEmailFields();
  const emailTemplateRef = useRef('');
  const templateWrapperRef = useRef<HTMLDivElement>(null);
  const [isEmailBodyInitialized, setIsEmailBodyInitialized] = useState(false);

  const emailBodyEventListener = useCallback(() => {
    const content = templateWrapperRef.current?.innerHTML ?? '';
    updateEmailBody(content ? DOMPurify.sanitize(content) : content);
  }, [updateEmailBody]);

  useEffect(() => {
    const elRef = templateWrapperRef.current;
    if (!elRef) {
      return;
    }

    elRef.addEventListener('keyup', emailBodyEventListener);

    return () => {
      elRef.removeEventListener('keyup', emailBodyEventListener);
    };
  }, [emailBodyEventListener]);

  useEffect(() => {
    const elRef = templateWrapperRef.current;
    if (isEmailBodyInitialized || !elRef) {
      return;
    }
    if (emailBody.trim() !== '') {
      emailTemplateRef.current = emailBody;
      elRef.innerHTML = DOMPurify.sanitize(emailBody);
      setIsEmailBodyInitialized(true);
    }
  }, [emailBody, isEmailBodyInitialized]);

  return (
    <form css={containerStyle}>
      <FormRow>
        <EmailField {...emailFieldProps} label={t('Email')} name='email' />
      </FormRow>
      <FormRow>
        <TextField {...emailSubjectFieldProps} label={t('Subject')} name='email-subject' />
      </FormRow>
      <FormRow css={[lastRowStyle, emailBodyFormRowStyle]}>
        <div
          ref={templateWrapperRef}
          contentEditable
          className={emailBody.length === 0 ? 'error' : ''}
          css={bodyContainerStyle}
        ></div>
        {emailBody.length === 0 && (
          <Text color='error' size='small' css={errorMessageStyle}>
            {t('Email body is required')}
          </Text>
        )}
      </FormRow>
    </form>
  );
};
